import moment from "moment";
import * as React from "react";
import Show from "../../../FormRegistration/components/Show";
import useWindowSize from "../../../../../hooks/useWindowSize";
import Skeleton from "react-loading-skeleton";
import { func } from "../../../../../utils";
import Lottie from "react-lottie";
import animationData from "./animationData.json";

export interface ScheduleCardProps {
  away_club_id: number;
  away_club_name: string;
  away_club_short_name: string;
  away_coach: string;
  away_picture_url: string;
  away_score: number;
  away_penalty_score: number;
  competition_name: string;
  competition_stage_group_name: string;
  competition_stage_name: string;
  copa_round_name: string;
  default_match_date: string;
  gameweek: number;
  home_club_id: number;
  home_club_name: string;
  home_club_short_name: string;
  home_coach: string;
  home_picture_url: string;
  home_score: number;
  home_penalty_score: number;
  id: number;
  is_qc_finished: number;
  last_update: string;
  match_date: string;
  match_status: string;
}

export interface SchoolProps {
  id: number;
  school_name: string;
  school_logo: string;
}

export interface NewScheduleCardProps {
  id: number;
  match_name: string;
  match_date: string;
  match_time: string;
  match_time_zone: string;
  match_pool: string;
  match_status: string;
  knockout_round: string;
  school_1: SchoolProps;
  school_2: SchoolProps;
  school_1_score: number;
  school_2_score: number;
  school_1_penalty_score?: number | null;
  school_2_penalty_score?: number | null;
}

export interface CardItemProps {
  isLoading?: boolean;
  bgColor: string;
  item: NewScheduleCardProps;
  radius?: string;
  onClick?: (data: ScheduleCardProps) => void;
  isForceHideStatisticButton?: boolean;
}

const imgPath = `${process.env.REACT_APP_API_HOST}/uploads/featured-images/`;

export const CardItem: React.FC<CardItemProps> = ({
  isLoading = false,
  bgColor = "#314358",
  radius = "",
  item,
  onClick,
  isForceHideStatisticButton = false,
}) => {
  const { size } = useWindowSize();
  // @ts-ignore
  const isMobileView = (size?.width || 0) <= 830;

  const dataHome = item?.school_1;
  const dataAway = item?.school_2;

  const homeTeam = dataHome?.school_name;
  const homeScore = item?.school_1_score;
  const homePenaltyScore = item?.school_1_penalty_score;
  const homeLogo = imgPath + dataHome?.school_logo;

  const awayTeam = dataAway?.school_name;
  const awayScore = item?.school_2_score;
  const awayPenaltyScore = item?.school_2_penalty_score;
  const awayLogo = imgPath + dataAway?.school_logo;
  // Aturannya disamain sama bagan knockout: skor adu penalti cuma muncul kalau skor
  // normalnya seri. Dicek `!= null`, bukan truthy, biar "(0)" tetap ketulis.
  const hasPenaltyShootout =
    homeScore === awayScore &&
    homePenaltyScore != null &&
    awayPenaltyScore != null &&
    homePenaltyScore !== awayPenaltyScore;

  const matchDateTime = `${item?.match_date} ${item?.match_time}`;
  const matchTime = !!matchDateTime
    ? `${moment(matchDateTime)?.format("H:mm")} ${item?.match_time_zone}`
    : "-";

  const getMatchName = (matchPool: string) => {
    const sanitizeMatchPoolFromNumber = (pool?: string) => {
      if (!pool) return "-";
      const cleaned = pool.replace(/\d+/g, "").replace(/\s+/g, " ").trim();
      return cleaned || "-";
    };

    const matchName = sanitizeMatchPoolFromNumber(matchPool);

    const MAPPING_GROUP = {
      QF: "Top 16",
      SF: "Quarter-Final",
      F: "Semi-Final",
      Champion: "Final",
      "SOC Round Robin": "Group Stage",
    };

    if (MAPPING_GROUP?.[matchName]) {
      if (
        ["Top 16", "Final", "Group Stage"]?.includes(MAPPING_GROUP?.[matchName])
      ) {
        return MAPPING_GROUP?.[matchName];
      } else {
        return (
          MAPPING_GROUP?.[matchName] +
          " " +
          matchPool?.slice(matchPool?.length - 1, matchPool?.length)
        );
      }
    } else {
      return matchPool;
    }
  };

  const groupName = getMatchName(item?.match_pool);

  const generateMatchStatus = () => {
    const matchStatus = String(item?.match_status)?.toLowerCase();

    if (matchStatus === "full time") return "Full Time";
    if (matchStatus === "akan datang") return "Akan Datang";
    if (matchStatus === "dibatalkan") return "Dibatalkan";

    return "Berlangsung";
  };

  const generateMatchLabelColor = () => {
    const matchStatus = String(item?.match_status).toLowerCase();

    if (matchStatus === "full time") return "bg-[#AF183C]";
    if (matchStatus === "akan datang") return "bg-[#1F2D3DB2]";
    if (matchStatus === "dibatalkan") return "bg-[#DA6014]";

    return "bg-[#77B341]";
  };

  const matchStatus = generateMatchStatus();
  const matchStatusLabelColor = generateMatchLabelColor();

  const styleClassname = React.useMemo(() => {
    if (isMobileView) {
      return {
        container: `flex justify-center items-center gap-[8px] p-[16px] ${radius}`,
        label: "rounded-[6px] py-[2px] px-[4px]",
        teamInformation:
          "flex flex-col gap-y-[8px] justify-center items-center w-[38%] h-[100%]",
      };
    }

    return {
      container: `flex justify-center items-center gap-[12px] max-w-[807px] p-[24px] ${radius}`,
      label: "rounded-[6px] py-[3px] px-[6px]",
      teamInformation:
        "flex flex-col gap-y-[12px] justify-center items-center w-[38%] h-[100%]",
    };
  }, [isMobileView]);

  const logoWidthAndHeight = isMobileView ? 40 : 60;
  const textSize = isMobileView ? `11px` : `13px`;

  const handleClick = (item) => {
    if (matchStatus === "Akan Datang" || matchStatus === "Dibatalkan") return;

    return onClick?.(item);
  };

  const handleErrorImage = (event: React.ChangeEvent<HTMLImageElement>) => {
    event.target.onerror = null;
    event.target.src = func.getStaticImg("illustration/default-tim-logo.png");
  };

  return (
    <div
      onClick={() => handleClick(item)}
      style={{ backgroundColor: bgColor }}
      className={styleClassname?.container}
    >
      <div className={styleClassname?.teamInformation}>
        <Show
          when={!isLoading}
          fallbackComponent={
            <React.Fragment>
              <Skeleton
                width={logoWidthAndHeight}
                height={logoWidthAndHeight}
                borderRadius="50%"
                enableAnimation
                baseColor="#52759E"
                highlightColor="#034B5F"
              />
              <Skeleton
                width="80px"
                height="16px"
                enableAnimation
                baseColor="#52759E"
                highlightColor="#034B5F"
              />
            </React.Fragment>
          }
        >
          <img
            src={homeLogo}
            alt="home team logo"
            width={logoWidthAndHeight}
            height={logoWidthAndHeight}
            onError={handleErrorImage}
          />
          <h1
            style={{ fontSize: textSize }}
            className="uppercase w-[90%] leading-[13px] m-0 p-0 text-white text-center line-clamp-2 text-ellipsis"
          >
            {homeTeam}
          </h1>
        </Show>
      </div>
      <div className="flex flex-col gap-y-[12px] justify-center items-center w-[24%] h-[100%]">
        <Show
          when={!isLoading}
          fallbackComponent={
            <React.Fragment>
              <Skeleton
                width="80px"
                height="14px"
                enableAnimation
                baseColor="#52759E"
                highlightColor="#034B5F"
                borderRadius={9}
              />
              <div className="flex gap-x-[8px]">
                <Skeleton
                  borderRadius={9}
                  width="50px"
                  height="14px"
                  enableAnimation
                  baseColor="#52759E"
                  highlightColor="#034B5F"
                />
                <Skeleton
                  borderRadius={"50%"}
                  width="14px"
                  height="14px"
                  enableAnimation
                  baseColor="#52759E"
                  highlightColor="#034B5F"
                />
              </div>
              <Skeleton
                width="90px"
                height="14px"
                enableAnimation
                baseColor="#52759E"
                highlightColor="#034B5F"
                borderRadius={9}
              />
            </React.Fragment>
          }
        >
          <h1
            style={{ fontSize: textSize }}
            className="uppercase leading-[13px] m-0 p-0 text-[#34CEF9] font-semibold"
          >
            {matchTime}
          </h1>
          <h1
            style={{ fontSize: textSize }}
            className="text-center uppercase leading-[13px] m-0 p-0 text-white"
          >
            {groupName}
          </h1>
          <Show when={matchStatus !== "Akan Datang"}>
            <div className="flex gap-x-[36px] p-[12px] mb-8">
              <p className="whitespace-nowrap text-[20px] text-white font-bold leading-[20px]">
                {homeScore} {hasPenaltyShootout ? ` (${homePenaltyScore})` : ""}
              </p>
              <p className="text-[20px] text-white font-bold leading-[20px]">
                -
              </p>
              <p className="whitespace-nowrap text-[20px] text-white font-bold leading-[20px]">
                {awayScore} {hasPenaltyShootout ? ` (${awayPenaltyScore})` : ""}
              </p>
            </div>
          </Show>
          <Show when={matchStatus !== "Berlangsung"}>
            <div
              className={`${styleClassname?.label} ${matchStatusLabelColor}`}
            >
              <h1
                style={{ fontSize: isMobileView ? 11 : 16 }}
                className="text-white  leading-[16px] uppercase text-center"
              >
                {matchStatus}
              </h1>
            </div>
          </Show>
          <Show when={matchStatus === "Berlangsung"}>
            <Lottie
              options={{
                animationData: animationData,
              }}
              style={{ flex: 1 }}
            />
          </Show>

          <Show
            when={matchStatus !== "Akan Datang" && !isForceHideStatisticButton}
          >
            <p
              style={{ fontSize: textSize }}
              className="cursor-pointer underline text-white text-center"
            >
              Detail Statistik
            </p>
          </Show>
        </Show>
      </div>
      <div className={styleClassname?.teamInformation}>
        <Show
          when={!isLoading}
          fallbackComponent={
            <React.Fragment>
              <Skeleton
                width={logoWidthAndHeight}
                height={logoWidthAndHeight}
                borderRadius="50%"
                enableAnimation
                baseColor="#52759E"
                highlightColor="#034B5F"
              />
              <Skeleton
                width="80px"
                height="16px"
                enableAnimation
                baseColor="#52759E"
                highlightColor="#034B5F"
              />
            </React.Fragment>
          }
        >
          <img
            src={awayLogo}
            alt="away team logo"
            width={logoWidthAndHeight}
            height={logoWidthAndHeight}
            onError={handleErrorImage}
          />
          <h1
            style={{ fontSize: textSize }}
            className="uppercase w-[90%] leading-[13px] m-0 p-0 text-white text-center line-clamp-2 text-ellipsis"
          >
            {awayTeam}
          </h1>
        </Show>
      </div>
    </div>
  );
};

export interface ScheduleCardListProps {
  isLoading: boolean;
  data: ScheduleCardProps[];
  onClick: (data: ScheduleCardProps) => void;
}

const ScheduleCardList: React.FC<ScheduleCardListProps> = ({
  isLoading = true,
  data = [],
  onClick,
}) => {
  const dataLoading = new Array(6).fill(null);

  if (isLoading) {
    return (
      <div className="my-[16px] rounded-[24px] bg-[#314358] min-h-[100px]">
        {dataLoading?.map((_, idx) => {
          const isOdd = idx % 2 !== 0;
          const isFirstCard = idx === 0;
          const isLastCard = idx === dataLoading?.length - 1;

          const bgColor = isOdd ? "#314358" : "#3E556E";
          const generateCardRadius = () => {
            if (isFirstCard) return "rounded-t-[24px]";
            if (isLastCard) return "rounded-b-[24px]";

            return "";
          };

          const cardRadius = generateCardRadius();

          return (
            <CardItem
              isLoading={isLoading}
              onClick={onClick}
              radius={cardRadius}
              bgColor={bgColor}
              key={idx}
              item={_}
            />
          );
        })}
      </div>
    );
  }

  return (
    <div className="my-[16px] rounded-[24px] bg-[#314358] min-h-[100px]">
      {data?.map((item, idx) => {
        const isOdd = idx % 2 !== 0;
        const isFirstCard = idx === 0;
        const isLastCard = idx === data?.length - 1;
        const isSingleCard = data?.length === 1;

        const bgColor = isOdd ? "#314358" : "#3E556E";
        const generateCardRadius = () => {
          if (isFirstCard) {
            if (isSingleCard) return "rounded-[24px]";
            return "rounded-t-[24px]";
          }
          if (isLastCard) return "rounded-b-[24px]";

          return "";
        };

        const cardRadius = generateCardRadius();

        return (
          <CardItem
            onClick={onClick}
            radius={cardRadius}
            bgColor={bgColor}
            key={item?.id}
            item={item as any}
          />
        );
      })}
    </div>
  );
};

export default ScheduleCardList;
